import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { teamsAPI } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import { 
  Building2, 
  Users, 
  Plus, 
  Settings, 
  Crown, 
  Shield, 
  User,
  Calendar,
  Globe,
  MapPin
} from 'lucide-react';

interface Team {
  id: number;
  name: string;
  industry?: string;
  minBudget?: number;
  maxBudget?: number;
  createdAt: string;
  updatedAt: string;
  role: string;
  joinedAt: string;
  memberCount: number;
}

export function TeamsPage() {
  const [createTeamOpen, setCreateTeamOpen] = useState(false);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data: teamsResponse, isLoading, error } = useQuery({
    queryKey: ['teams'],
    queryFn: () => teamsAPI.getTeams(),
  });

  const teams = teamsResponse?.data as Team[] | undefined;

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'owner':
        return <Crown className="h-4 w-4 text-yellow-600" />;
      case 'admin':
        return <Shield className="h-4 w-4 text-blue-600" />;
      case 'member':
        return <User className="h-4 w-4 text-gray-600" />;
      default:
        return <User className="h-4 w-4 text-gray-600" />;
    }
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'owner':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'admin':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'member':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatCurrency = (amount?: number) => {
    if (!amount) return 'Not set';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Teams</h1>
            <p className="text-muted-foreground">Manage your teams and switch between workspaces</p>
          </div>
        </div>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-6">
                <div className="space-y-3">
                  <div className="h-6 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                  <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Teams</h1>
            <p className="text-muted-foreground">Manage your teams and switch between workspaces</p>
          </div>
        </div>
        <Alert variant="destructive">
          <AlertDescription>
            Failed to load teams: {(error as Error).message}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Teams</h1>
          <p className="text-muted-foreground">
            Manage your teams and switch between workspaces
          </p>
        </div>
        <Dialog open={createTeamOpen} onOpenChange={setCreateTeamOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Create Team
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New Team</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <p className="text-sm text-muted-foreground">
                Team creation will redirect you to the full onboarding flow to set up your agency profile.
              </p>
              <div className="mt-4 flex justify-end space-x-2">
                <Button variant="outline" onClick={() => setCreateTeamOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={() => {
                  setCreateTeamOpen(false);
                  window.location.href = '/onboarding';
                }}>
                  Continue to Onboarding
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {!teams || teams.length === 0 ? (
        <div className="text-center py-12">
          <Building2 className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-medium mb-2">No teams found</h3>
          <p className="text-muted-foreground mb-6">
            Create your first team to get started with LeadGPT.
          </p>
          <Button onClick={() => setCreateTeamOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Create Your First Team
          </Button>
        </div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {teams.map((team) => (
            <Card key={team.id} className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <Building2 className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{team.name}</CardTitle>
                      {team.industry && (
                        <CardDescription className="flex items-center mt-1">
                          <Globe className="h-3 w-3 mr-1" />
                          {team.industry}
                        </CardDescription>
                      )}
                    </div>
                  </div>
                  <Badge 
                    variant="outline" 
                    className={getRoleBadgeColor(team.role)}
                  >
                    <span className="flex items-center space-x-1">
                      {getRoleIcon(team.role)}
                      <span className="capitalize">{team.role}</span>
                    </span>
                  </Badge>
                </div>
              </CardHeader>
              
              <CardContent className="pt-0">
                <div className="space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center text-muted-foreground">
                      <Users className="h-4 w-4 mr-1" />
                      Members
                    </span>
                    <span className="font-medium">{team.memberCount}</span>
                  </div>
                  
                  {(team.minBudget || team.maxBudget) && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Budget Range</span>
                      <span className="font-medium">
                        {formatCurrency(team.minBudget)} - {formatCurrency(team.maxBudget)}
                      </span>
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between text-sm">
                    <span className="flex items-center text-muted-foreground">
                      <Calendar className="h-4 w-4 mr-1" />
                      Joined
                    </span>
                    <span className="font-medium">{formatDate(team.joinedAt)}</span>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t">
                  <div className="flex space-x-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="flex-1"
                      onClick={() => {
                        // Switch to this team context
                        toast({
                          title: 'Team Selected',
                          description: `Switched to ${team.name}`,
                        });
                        // In a real app, you would update the user's current team context
                        window.location.href = '/dashboard';
                      }}
                    >
                      Switch To
                    </Button>
                    {(team.role === 'owner' || team.role === 'admin') && (
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => {
                          toast({
                            title: 'Team Settings',
                            description: 'Team settings functionality coming soon',
                          });
                        }}
                      >
                        <Settings className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}